import React from "react";
import { defaultTextFormat, EDITABLE_TEXT_BLOCK_TYPES, FORMATTABLE_BLOCK_TYPES } from "./payslipTemplateDefaults";
import {
  getColorKeysForGroup,
  isValidHexColor,
  normalizeCustomHex,
  PAYSLIP_COLOR_GROUP_ORDER,
  PAYSLIP_TEXT_COLOR_CUSTOM,
  PAYSLIP_TEXT_COLORS,
  resolveTextColorHex,
} from "./payslipTextColors";

/** @typedef {import('./payslipTemplateDefaults').PayslipBlock} PayslipBlock */

export { FORMATTABLE_BLOCK_TYPES, EDITABLE_TEXT_BLOCK_TYPES } from "./payslipTemplateDefaults";

/** @param {string} type */
export const supportsTextFormatting = (type) => FORMATTABLE_BLOCK_TYPES.has(type);

/** @param {string} type */
export const supportsTextEditing = (type) => EDITABLE_TEXT_BLOCK_TYPES.has(type);

/** @param {PayslipBlock} block */
export const ensureTextFormat = (block) => ({
  ...defaultTextFormat(),
  ...block,
  textTransform: block.textTransform === "uppercase" || block.textTransform === "capitalize" ? block.textTransform : "none",
  textColor: block.textColor || "default",
});

/**
 * @param {PayslipBlock} block
 * @param {Record<string, string>} fallbacks
 */
export const getBlockDisplayText = (block, fallbacks) => {
  const custom = block.text != null ? String(block.text).trim() : "";
  if (custom) return custom;
  return fallbacks[block.type] || "";
};

/**
 * @param {PayslipBlock} block
 * @returns {React.CSSProperties}
 */
export const getBlockTextStyle = (block) => {
  const transform =
    block.textTransform === "uppercase"
      ? "uppercase"
      : block.textTransform === "capitalize"
        ? "capitalize"
        : "none";

  return {
    fontSize: block.fontSize === "sm" ? "0.72rem" : block.fontSize === "lg" ? "1rem" : "0.82rem",
    fontWeight: block.fontWeight === "bold" ? 700 : 400,
    fontStyle: block.fontStyle === "italic" ? "italic" : "normal",
    textTransform: transform,
    color: resolveTextColorHex(block),
  };
};

const colorLabelKey = (colorId) =>
  `app.payroll.payslip.textColor${colorId.charAt(0).toUpperCase()}${colorId.slice(1)}`;

/**
 * @param {{
 *   block: PayslipBlock,
 *   onChange: (next: PayslipBlock) => void,
 *   t: (key: string, fallback?: string) => string,
 *   textPlaceholder?: string,
 *   textHint?: string,
 * }} props
 */
const PayslipTextFormatInspector = ({ block, onChange, t, textPlaceholder, textHint }) => {
  const formatted = ensureTextFormat(block);
  const set = (patch) => onChange({ ...formatted, ...patch });
  const activeColor = formatted.textColor || "default";
  const customHex = normalizeCustomHex(formatted.customTextColor);

  return (
    <>
      {supportsTextEditing(block.type) ? (
        <>
          <label className="form-label small" htmlFor="payslip-block-text">
            {block.type === "customText" ? t("app.payroll.payslip.customText") : t("app.payroll.payslip.overrideText")}
          </label>
          <textarea
            id="payslip-block-text"
            className="form-control form-control-sm mb-1"
            rows={block.type === "customText" ? 3 : 2}
            placeholder={textPlaceholder}
            value={formatted.text || ""}
            onChange={(e) => set({ text: e.target.value })}
          />
          {textHint ? <p className="small text-muted mb-2">{textHint}</p> : null}
        </>
      ) : null}

      <label className="form-label small">{t("app.payroll.payslip.textFormatting")}</label>

      <div className="payroll-payslip-inspector__format-row mb-2">
        <span className="small text-muted">{t("app.payroll.payslip.fontSize")}</span>
        <div className="btn-group btn-group-sm" role="group">
          {(["sm", "md", "lg"]).map((size) => (
            <button
              key={size}
              type="button"
              className={`btn btn-outline-secondary${(formatted.fontSize || "md") === size ? " active" : ""}`}
              onClick={() => set({ fontSize: size })}
            >
              {t(`app.payroll.payslip.fontSize${size.charAt(0).toUpperCase()}${size.slice(1)}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="payroll-payslip-inspector__format-row mb-2">
        <span className="small text-muted">{t("app.payroll.payslip.fontStyle")}</span>
        <div className="btn-group btn-group-sm" role="group">
          <button
            type="button"
            className={`btn btn-outline-secondary${formatted.fontWeight === "bold" ? " active" : ""}`}
            onClick={() => set({ fontWeight: formatted.fontWeight === "bold" ? "normal" : "bold" })}
            aria-pressed={formatted.fontWeight === "bold"}
            title={t("app.payroll.payslip.bold")}
          >
            <i className="bi bi-type-bold" aria-hidden />
          </button>
          <button
            type="button"
            className={`btn btn-outline-secondary${formatted.fontStyle === "italic" ? " active" : ""}`}
            onClick={() => set({ fontStyle: formatted.fontStyle === "italic" ? "normal" : "italic" })}
            aria-pressed={formatted.fontStyle === "italic"}
            title={t("app.payroll.payslip.italic")}
          >
            <i className="bi bi-type-italic" aria-hidden />
          </button>
        </div>
      </div>

      <div className="payroll-payslip-inspector__format-row mb-2">
        <span className="small text-muted">{t("app.payroll.payslip.textTransform")}</span>
        <div className="btn-group btn-group-sm" role="group">
          <button
            type="button"
            className={`btn btn-outline-secondary${formatted.textTransform === "none" ? " active" : ""}`}
            onClick={() => set({ textTransform: "none" })}
            title={t("app.payroll.payslip.transformNone")}
          >
            Aa
          </button>
          <button
            type="button"
            className={`btn btn-outline-secondary${formatted.textTransform === "uppercase" ? " active" : ""}`}
            onClick={() => set({ textTransform: "uppercase" })}
            title={t("app.payroll.payslip.transformUppercase")}
          >
            AA
          </button>
          <button
            type="button"
            className={`btn btn-outline-secondary${formatted.textTransform === "capitalize" ? " active" : ""}`}
            onClick={() => set({ textTransform: "capitalize" })}
            title={t("app.payroll.payslip.transformCapitalize")}
          >
            AaBb
          </button>
        </div>
      </div>

      <label className="form-label small">{t("app.payroll.payslip.textColor")}</label>
      <div className="payroll-payslip-inspector__color-panel mb-3">
        {PAYSLIP_COLOR_GROUP_ORDER.map((groupId) => {
          const keys = getColorKeysForGroup(groupId);
          if (!keys.length) return null;
          return (
            <div key={groupId} className="payroll-payslip-inspector__color-group">
              <span className="payroll-payslip-inspector__color-group-label">
                {t(`app.payroll.payslip.colorGroup${groupId.charAt(0).toUpperCase()}${groupId.slice(1)}`)}
              </span>
              <div className="payroll-payslip-inspector__color-swatches">
                {keys.map((colorId) => (
                  <button
                    key={colorId}
                    type="button"
                    className={`payroll-payslip-inspector__color-swatch${
                      activeColor === colorId ? " is-active" : ""
                    }`}
                    style={{ background: PAYSLIP_TEXT_COLORS[colorId].hex }}
                    title={t(colorLabelKey(colorId), colorId)}
                    onClick={() => set({ textColor: colorId, customTextColor: undefined })}
                  />
                ))}
              </div>
            </div>
          );
        })}

        <div className="payroll-payslip-inspector__color-group">
          <span className="payroll-payslip-inspector__color-group-label">
            {t("app.payroll.payslip.colorGroupCustom")}
          </span>
          <div className="payroll-payslip-inspector__custom-color">
            <button
              type="button"
              className={`payroll-payslip-inspector__color-swatch payroll-payslip-inspector__color-swatch--custom${
                activeColor === PAYSLIP_TEXT_COLOR_CUSTOM ? " is-active" : ""
              }`}
              style={{
                background:
                  activeColor === PAYSLIP_TEXT_COLOR_CUSTOM
                    ? customHex
                    : "conic-gradient(#ef4444, #f59e0b, #22c55e, #3b82f6, #a855f7, #ef4444)",
              }}
              title={t("app.payroll.payslip.textColorCustom")}
              onClick={() => set({ textColor: PAYSLIP_TEXT_COLOR_CUSTOM, customTextColor: customHex })}
            />
            <div className="payroll-payslip-inspector__custom-color-inputs">
              <input
                type="color"
                className="form-control form-control-color payroll-payslip-inspector__color-picker"
                value={customHex}
                onChange={(e) =>
                  set({ textColor: PAYSLIP_TEXT_COLOR_CUSTOM, customTextColor: normalizeCustomHex(e.target.value) })
                }
                aria-label={t("app.payroll.payslip.customColorPicker")}
              />
              <input
                type="text"
                className="form-control form-control-sm payroll-payslip-inspector__hex-input"
                value={activeColor === PAYSLIP_TEXT_COLOR_CUSTOM ? customHex : ""}
                placeholder="#6366f1"
                maxLength={7}
                onChange={(e) => {
                  const next = e.target.value.trim();
                  if (!next) return;
                  const normalized = normalizeCustomHex(next);
                  if (isValidHexColor(normalized)) {
                    set({ textColor: PAYSLIP_TEXT_COLOR_CUSTOM, customTextColor: normalized });
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PayslipTextFormatInspector;
